/**
 * FIFO cost on a release, as findAllRelease returns it: `cost` is what the
 * released units cost when they left stock (oldest lot first), `uncostedQty`
 * counts units that left with no lot behind them. A release with uncosted
 * units shows a dash rather than a number that is too low.
 */
import { formatAmount } from "../../purchaseListUtils";

export interface CostedRelease {
  cost?: number;
  uncostedQty?: number;
  netTotal?: number;
}

const DASH = "—";

export const hasUncosted = (item: CostedRelease | null | undefined) => Number(item?.uncostedQty) > 0;

/** "120.00", or "—" when part of the release has no cost behind it. */
export const costLabel = (item: CostedRelease | null | undefined): string => {
  if (!item || hasUncosted(item)) return DASH;
  return formatAmount(Number(item.cost) || 0);
};

/** Net total minus cost for a sold release; null when the cost is unknown. */
export const marginOf = (item: CostedRelease | null | undefined): number | null => {
  if (!item || hasUncosted(item)) return null;
  return (Number(item.netTotal) || 0) - (Number(item.cost) || 0);
};

export const marginLabel = (item: CostedRelease | null | undefined): string => {
  const margin = marginOf(item);
  return margin == null ? DASH : formatAmount(margin);
};

/** Tailwind text colour for a margin: red when selling under cost. */
export const marginTone = (item: CostedRelease | null | undefined): string => {
  const margin = marginOf(item);
  if (margin == null) return "text-slate-400 dark:text-slate-500";
  return margin < 0 ? "text-red-500 dark:text-red-400" : "text-emerald-600 dark:text-emerald-300";
};
